import type { ConversationDetail, ConversationMessage, WsEvent } from '@chatbot/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGlobalWebSocket } from '../../hooks/WebSocketProvider.tsx';
import { conversationsApi } from '../../lib/conversations-api.ts';
import { toast } from '../Toast.tsx';

export interface ConversationThreadResult {
  conv: ConversationDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Carga y mantiene vivo el hilo de UNA conversación (la seleccionada en el
 * workspace). Los mensajes nuevos llegan por WebSocket y se appendean sin
 * refetch; los cambios de estado sí disparan refetch.
 */
export function useConversationThread(
  tenantId: string | null,
  conversationId: string | null,
): ConversationThreadResult {
  const [conv, setConv] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { subscribe, lastReconnectAt } = useGlobalWebSocket();
  // Descarta respuestas de una selección anterior que llegan tarde
  const requestKey = useRef<string>('');

  const refetch = useCallback(async () => {
    if (!tenantId || !conversationId) {
      requestKey.current = '';
      setConv(null);
      setError(null);
      setLoading(false);
      return;
    }
    const key = `${tenantId}#${conversationId}`;
    requestKey.current = key;
    // Al cambiar de conversación descartamos el hilo previo para no mostrarlo
    // stale mientras carga el nuevo; en un refetch de la misma lo conservamos.
    setConv((prev) => (prev && `${prev.tenantId}#${prev.conversationId}` === key ? prev : null));
    setLoading(true);
    setError(null);
    try {
      const data = await conversationsApi.get(tenantId, conversationId);
      if (requestKey.current !== key) return;
      setConv(data);
    } catch (err) {
      if (requestKey.current !== key) return;
      setError((err as Error).message);
      toast.error(`Error al cargar conversación: ${(err as Error).message}`);
    } finally {
      if (requestKey.current === key) setLoading(false);
    }
  }, [tenantId, conversationId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (lastReconnectAt) refetch();
  }, [lastReconnectAt, refetch]);

  useEffect(() => {
    if (!tenantId || !conversationId) return;
    return subscribe((event: WsEvent) => {
      if (event.tenantId !== tenantId || event.conversationId !== conversationId) return;

      if (event.type === 'message.received' || event.type === 'message.sent') {
        setConv((prev) => {
          if (!prev) return prev;
          const incoming = eventToMessage(event);
          const dup = prev.messages.some(
            (m) =>
              m.id === incoming.id ||
              (m.timestamp === incoming.timestamp && m.text === incoming.text),
          );
          return dup ? prev : { ...prev, messages: [...prev.messages, incoming] };
        });
      } else if (
        // `conversation.escalated` faltaba, y era el único de los tres que
        // cambia el estado sin que nadie toque el panel. Con la conversación
        // abierta, el botón «Tomar» no aparecía hasta recargar la página
        // (GKTIBO-103) — la lista se enteraba y esto no.
        event.type === 'conversation.escalated' ||
        event.type === 'state.changed' ||
        event.type === 'admin.takeover'
      ) {
        refetch();
      }
    });
  }, [tenantId, conversationId, subscribe, refetch]);

  return { conv, loading, error, refetch };
}

function eventToMessage(
  event: Extract<WsEvent, { type: 'message.received' | 'message.sent' }>,
): ConversationMessage {
  return {
    id: `${event.payload.timestamp}-${event.payload.role}-${Math.random().toString(36).substring(2, 6)}`,
    role: event.payload.role,
    text: event.payload.text,
    timestamp: event.payload.timestamp,
    adminId: 'adminId' in event.payload ? event.payload.adminId : undefined,
    adminName: 'adminName' in event.payload ? event.payload.adminName : undefined,
  };
}
